/**
 * Write a base folder: `base.yaml` plus one file per config entity, secrets
 * stripped — the `pull` half of the round-trip `config-parser.ts` reads back.
 *
 * Field order is fixed per entity kind so YAML diffs stay minimal, and the write
 * is idempotent: files for entities the server no longer has are removed, so the
 * folder mirrors the base rather than accumulating.
 */

import * as fs from 'node:fs';
import * as path from 'node:path';
import * as yaml from 'js-yaml';
import { expected } from '../../lib/expected.js';
import { YAML_DUMP_OPTIONS, writeFileIfChanged } from '../../lib/yaml-writer.js';
import {
  ensureRealSubdirNoSymlink,
  readFileNoFollow,
  writeFileNoFollow,
  type FileChangeLog,
} from '../../lib/fs-safety.js';
import { BASE_META_FILE } from '../../lib/base-workspace.js';
import { isPathSafeId } from '../../lib/base-id.js';
import {
  DEPRECATED_ENTITY_DIRS,
  ENTITY_DIRS,
  ENTITY_KINDS,
  type BaseConfig,
  type BaseMeta,
  type EntityConfig,
  type EntityKind,
} from './types.js';

function dump(value: unknown): string {
  return yaml.dump(value, YAML_DUMP_OPTIONS);
}

/**
 * Drop `undefined`-valued keys so an absent field doesn't render as `null` in
 * YAML — and, where the result is SPREAD over an existing meta, so a field the
 * server omitted cannot wipe one the folder declares.
 */
export function omitUndefined(obj: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(obj)) {
    if (v !== undefined) out[k] = v;
  }
  return out;
}

/** Strip every `auth.secret` from a record type's sources — secrets are never written to disk. */
function stripRecordTypeSources(sources: unknown): unknown[] | undefined {
  if (!Array.isArray(sources)) return undefined;
  return sources.map((raw) => {
    const source = raw as Record<string, unknown>;
    const auth = source.auth as Record<string, unknown> | undefined;
    if (!auth) return source;
    const { secret: _secret, ...restAuth } = auth;
    return { ...source, auth: restAuth };
  });
}

/**
 * The fields written for each entity kind, in file order.
 *
 * This is a deliberate ALLOWLIST, not an oversight: `secret` is absent from
 * `inbound_webhooks` and `triggers` because an ingest token is shown once at
 * creation and must never reach disk, and `record_types.sources` is rewritten by
 * `stripRecordTypeSources` for the same reason. A pass-through writer would put
 * those bytes in the user's repo.
 *
 * The cost of an allowlist is that a field missing from it is silently ERASED
 * from the user's committed YAML on the next pull — and, because the config is
 * the full desired state, REMOVED from the base on the next push. So each list
 * is checked for exhaustiveness against its own type below: adding a field to
 * `types.ts` without listing it here is a typecheck failure, not data loss in
 * someone's repo.
 */
const ENTITY_FIELD_ORDER = {
  record_types: ['id', 'name', 'description', 'icon', 'color', 'json_schema', 'ui', 'sources'],
  relationship_types: [
    'id',
    'description',
    'data_schema',
    'source_record_types',
    'target_record_types',
    'cascade',
  ],
  file_types: ['id', 'name', 'description', 'icon', 'color', 'max_size', 'allowed_content_types', 'metadata_schema'],
  inbound_webhooks: [
    'id',
    'name',
    'record_type_scope',
    'field_mapping',
    'source_binding',
    'ingest_auth',
    'hydration',
    'enabled',
  ],
  triggers: [
    'id',
    'name',
    'action',
    'url',
    'events',
    'record_type_scope',
    'filter',
    'skip_inbound_webhook_writes',
    'skip_cascade_writes',
    'enabled',
  ],
  toolsets: ['id', 'name', 'description', 'actions', 'relationships', 'files', 'batch', 'sql_query', 'customer_scope'],
  actions: [
    'id',
    'record_type',
    'operation',
    'steps',
    'description',
    'external_source',
    'writable_fields',
    'precondition',
    'binding',
    'filterable_fields',
    'expose_filter',
    'expose_sort',
    'default_sort',
    'expose_limit',
    'default_limit',
    'expose_offset',
    'expose_fields',
    'default_fields',
    'agent_minimal',
    'base_filter',
    // A SQL Action: `sql` last, so its query reads as one block at the end of the file.
    'record_types',
    'params',
    'limit',
    'sql',
  ],
  seeds: ['id', 'description', 'records', 'relationships', 'exclusive_record_types'],
} as const satisfies { [K in EntityKind]: readonly (keyof EntityConfig<K>)[] };

/** Every field of every entity kind must appear above; an omission is a typecheck failure. */
type _FieldOrderIsExhaustive = {
  [K in EntityKind]: Exclude<
    keyof EntityConfig<K>,
    (typeof ENTITY_FIELD_ORDER)[K][number]
  > extends never
    ? true
    : ['ENTITY_FIELD_ORDER is missing keys for', K, Exclude<keyof EntityConfig<K>, (typeof ENTITY_FIELD_ORDER)[K][number]>];
};
const _fieldOrderExhaustive: _FieldOrderIsExhaustive = {
  record_types: true,
  relationship_types: true,
  file_types: true,
  inbound_webhooks: true,
  triggers: true,
  toolsets: true,
  actions: true,
  seeds: true,
};
void _fieldOrderExhaustive;

/** The ordered, secret-free file object for one entity. */
function toFileObject(kind: EntityKind, entity: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const key of ENTITY_FIELD_ORDER[kind] as readonly string[]) {
    // The one field that is REWRITTEN rather than copied: a record type's
    // sources carry `auth.secret`, which never goes to disk.
    const value = kind === 'record_types' && key === 'sources'
      ? stripRecordTypeSources(entity.sources)
      : entity[key];
    if (value !== undefined) out[key] = value;
  }
  return out;
}

/**
 * Remove `*.yaml` files in `dir` that the base no longer has, recording each in
 * `log.removed`. One `readdir`: the listing that decides what to delete is the
 * same one that reports it.
 */
function pruneOrphans(folder: string, dir: string, keep: Set<string>, log: FileChangeLog): void {
  // Bounded exactly like the write half. `readdirSync` and `rmSync` both FOLLOW
  // a symlink, so an entity dir symlinked out of the repo would have its files
  // listed and DELETED here — and when the base declares nothing of that kind
  // the keep-set is empty, so all of them go.
  if (!ensureRealSubdirNoSymlink(folder, dir, false)) return;
  let entries: string[];
  try {
    entries = fs.readdirSync(dir);
  } catch {
    return;
  }
  for (const file of entries) {
    if (!file.endsWith('.yaml') || keep.has(file)) continue;
    const abs = path.join(dir, file);
    fs.rmSync(abs);
    log.removed.push(path.relative(folder, abs));
  }
}

/**
 * `metaFileObject` whitelists by this list, so a key missing here is silently
 * ERASED from the user's committed `base.yaml` on the next write-back, not
 * merely ignored. `(keyof BaseMeta)[]` only checks that entries ARE valid keys,
 * not that every key is present — the exhaustiveness check below turns the next
 * omission into a typecheck failure instead of data loss in a user's file.
 */
const META_ORDER = [
  'base_id',
  'origin_base_id',
  'name',
  'description',
  'environment',
  'integrations',
  'analytics',
] as const satisfies readonly (keyof BaseMeta)[];

type _MetaOrderIsExhaustive = Exclude<keyof BaseMeta, (typeof META_ORDER)[number]> extends never
  ? true
  : ['META_ORDER is missing BaseMeta keys:', Exclude<keyof BaseMeta, (typeof META_ORDER)[number]>];
const _metaOrderExhaustive: _MetaOrderIsExhaustive = true;
void _metaOrderExhaustive;

function metaFileObject(meta: BaseMeta): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const key of META_ORDER) {
    if (meta[key] !== undefined) out[key] = meta[key];
  }
  return out;
}

/**
 * Write `base.yaml` and every entity file, removing what the base no longer has.
 * Returns the working-tree delta so the caller can name the files that changed
 * rather than announcing a silent rewrite.
 *
 * **Every write is bounded by `folder`, and none follows a symlink.** A
 * workspace is a git checkout, so it can be attacker-authored: a committed
 * `record-types/contact.yaml -> ~/.ssh/authorized_keys`, or a symlinked
 * `record-types/` directory, would otherwise let a teammate's or a CI job's
 * `wayai pull` overwrite a file outside the repo — the threat model
 * `lib/fs-safety.ts` states and every hub-as-code write already respects. Entity
 * ids are server-supplied (whoever can author the base's config chooses them),
 * so each is validated as a slug before it becomes a filename.
 */
export function writeBaseFolder(
  folder: string,
  meta: BaseMeta,
  config: BaseConfig,
): FileChangeLog {
  const delta: FileChangeLog = { changed: [], removed: [] };

  // The base folder is the containment root for every write below, so it has to
  // be a REAL directory, not one reached through a symlink. Its parent
  // (`wayai-ws/bases`) is spelled entirely from the layout descriptor — no user
  // or server input in any segment — so it is created plainly, exactly as the
  // hub side creates a hub folder's parent before materializing into it; the
  // no-follow walk then starts there and covers the one segment that is a name.
  const parent = path.dirname(folder);
  fs.mkdirSync(parent, { recursive: true });
  if (!ensureRealSubdirNoSymlink(parent, folder, true)) {
    throw expected(
      `Refusing to write ${folder}: the path crosses a symlink. Remove it and pull again.`,
    );
  }

  writeFileIfChanged(folder, path.join(folder, BASE_META_FILE), dump(metaFileObject(meta)), delta);

  for (const kind of ENTITY_KINDS) {
    const dir = path.join(folder, ENTITY_DIRS[kind]);
    const entities: readonly unknown[] = config[kind] ?? [];
    const keep = new Set<string>();
    for (const raw of entities) {
      const entity = raw as Record<string, unknown>;
      const id = String(entity.id);
      // `isPathSafeId`, not the base rule: these are entity ids the API does not
      // charset-check, so a dotted `record_type` is a legitimate value the server
      // returns. Checking them against the base rule would silently DROP such an
      // entity from the pull — the skip below is a warning, not an error.
      if (!isPathSafeId(id)) {
        // Skip-with-warning rather than throw: one malformed id must not cost
        // the user every other entity in an otherwise-good pull.
        console.warn(`  Warning: skipping ${kind} entity with unusable id ${JSON.stringify(id)}.`);
        continue;
      }
      const file = `${id}.yaml`;
      keep.add(file);
      writeFileIfChanged(folder, path.join(dir, file), dump(toFileObject(kind, entity)), delta);
    }
    pruneOrphans(folder, dir, keep, delta);
  }

  // Kinds that no longer exist leave dirs the writer and parser both ignore;
  // without this they linger as dead weight through every pull.
  for (const deprecated of DEPRECATED_ENTITY_DIRS) {
    const dir = path.join(folder, deprecated);
    // Bounded like every other write: never `rm -rf` through a symlinked path.
    if (ensureRealSubdirNoSymlink(folder, dir, false)) {
      fs.rmSync(dir, { recursive: true, force: true });
    }
  }

  return delta;
}

/** Leading marker on a mirrored production `base.yaml`. YAML comments are ignored by the parser. */
const MIRROR_MARKER_PREFIX = '# Read-only mirror of production base';

/**
 * Prepend the read-only banner to a freshly written production mirror — a human
 * signal alongside the machine-readable `environment: production` the writer
 * already emitted. Idempotent; a no-op when `base.yaml` is missing. Cosmetic:
 * push's own production refusal is the real protection.
 */
export function markProductionMirror(folder: string, baseId: string): void {
  const file = path.join(folder, BASE_META_FILE);
  try {
    const body = readFileNoFollow(folder, file)?.toString('utf-8');
    if (body === undefined || body.startsWith(MIRROR_MARKER_PREFIX)) return;
    const banner = `${MIRROR_MARKER_PREFIX} "${baseId}". Edits are ignored; \`wayai push\` refuses it. Edit the linked preview instead.\n`;
    // Cosmetic, but still no-follow: it must not be the one write that escapes.
    writeFileNoFollow(folder, file, Buffer.from(banner + body, 'utf-8'));
  } catch {
    // Best-effort by contract: the mirror is already written, and the banner is
    // a human hint. `writeFileNoFollow` can still throw on the underlying write
    // (an EISDIR, a read-only mount), and that must not fail the pull.
  }
}

/** Folder-local `.gitignore` for a production mirror: ignore everything, itself included. */
const MIRROR_GITIGNORE = '*\n';

/**
 * Git-ignore a read-only production-mirror folder so its regenerated contents
 * never surface as untracked churn in `git status` and never pollute
 * preview-iteration commits. The mirror is deterministically regenerated
 * reference material, not authored content — treated like build output. A
 * folder-local `.gitignore` of `*` ignores every file INCLUDING itself, so git
 * hides the whole directory, and it disappears with the mirror.
 */
export function ensureMirrorIgnored(folder: string): void {
  // A committed `.gitignore -> …` in the mirror folder would otherwise be the
  // one write in this module that still followed a symlink.
  try {
    writeFileNoFollow(folder, path.join(folder, '.gitignore'), Buffer.from(MIRROR_GITIGNORE, 'utf-8'));
  } catch {
    // Best-effort by contract: a `.gitignore` that is itself a directory (git
    // tracks `.gitignore/x`, so a branch can carry one) makes the write throw
    // EISDIR. A mirror that is merely noisy in `git status` is not worth failing
    // an otherwise-successful pull — nor reaching Sentry as an unexpected crash.
  }
}
